import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Package2, Tags, DollarSign, Factory, Edit, Cog, Clock, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useInventory } from '../context/InventoryContext';
import ProductManufacturingForm from '../components/products/ProductManufacturingForm';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import SupplyThumbnail from '../components/products/SupplyThumbnail';
import ImagePreviewModal from '../components/ui/ImagePreviewModal';
import ManufacturedProductLocationManager from '../components/locations/ManufacturedProductLocationManager';

const ProductManufacturingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCategoryName, products: supplies } = useInventory();
  const [showEditForm, setShowEditForm] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string; description?: string } | null>(null);
  
  const isNewProduct = id === 'new';

  useEffect(() => {
    if (!isNewProduct && id) {
      loadProduct();
    } else {
      setLoading(false);
    }
  }, [id, isNewProduct]);

  const loadProduct = async () => {
    try {
      try {
        const { data, error } = await supabase
          .from('manufactured_products')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (error && error.code === '42P01') {
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setProduct(null);
        } else if (error) {
          throw error;
        } else {
          setProduct(data);
        }
      } catch (tableError: any) {
        if (tableError.code === '42P01') {
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setProduct(null);
        } else {
          throw tableError;
        }
      }
    } catch (error) {
      console.error('Error loading manufactured product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!isNewProduct && !product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
        <p className="text-gray-500 mb-6">The manufactured product you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
      </div>
    );
  }

  if (isNewProduct || showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/products')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewProduct ? 'Create New Product' : `Edit Product: ${product?.name}`}
          </h1>
        </div>
        
        <ProductManufacturingForm 
          product={product} 
          isEdit={!isNewProduct} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/products')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/products" className="text-sm text-gray-500 hover:text-gray-700">
                  Products
                </Link>
              </li>
              <li>
                <span className="text-sm text-gray-600">/</span>
              </li>
              <li>
                <span className="text-sm text-gray-900">{product?.name}</span>
              </li>
            </ol>
          </nav>
        </div>
        <Button onClick={() => setShowEditForm(true)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Product
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="aspect-w-16 aspect-h-9 mb-6">
              {product?.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-contain bg-gray-50 rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Package2 className="h-20 w-20 text-blue-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-blue-800">Manufactured Product</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{product?.name}</h1>
                <Badge variant={product?.status === 'production' ? 'success' : 'warning'}>
                  {product?.status}
                </Badge>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {getCategoryName(product?.category_id || '')}
                </Badge>
                <span className="text-sm text-gray-500">SKU: {product?.sku}</span>
              </div>

              <p className="text-gray-600">{product?.description}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Selling Price</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">
                    ${product?.price?.toFixed(2)}
                  </span>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Package2 className="h-4 w-4" />
                    <span className="text-sm">Stock</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">
                    {product?.stock_quantity}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Manufacturing Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-200">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Production Time</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.production_time} minutes
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Batch Size</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.batch_size} units
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Production Cost</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    ${product?.cost_price?.toFixed(2)}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.location || 'Not set'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Min Stock Level</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.min_stock_level}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bill of Materials</CardTitle>
            </CardHeader>
            <CardContent>
              {product?.bill_of_materials?.length > 0 ? (
                <div className="space-y-3">
                  {[...product.bill_of_materials]
                    .sort((a: any, b: any) => (a.supply_name || '').localeCompare(b.supply_name || ''))
                    .map((item: any, index: number) => {
                    const supply = supplies.find(s => s.id === item.supply_id || s.sku === item.supply_sku);

                    return (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <SupplyThumbnail
                          imageUrl={supply?.image}
                          alt={item.supply_name}
                          size="md"
                          showZoom={!!supply?.image}
                          onClick={() => {
                            if (supply?.image) {
                              setPreviewImage({
                                url: supply.image,
                                title: item.supply_name,
                                description: `SKU: ${item.supply_sku} | Quantity: ${item.quantity} units per product`
                              });
                            }
                          }}
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.supply_name}</p>
                            <p className="text-xs text-gray-500">SKU: {item.supply_sku}</p>
                            {supply && (
                              <p className="text-xs text-blue-600 mt-1">
                                Available: {supply.stock_quantity} units
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">{item.quantity} units</p>
                            <p className="text-xs text-gray-500">per product</p>
                            {item.cost_per_unit && (
                              <p className="text-xs text-gray-500 mt-1">
                                ${(item.cost_per_unit * item.quantity).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No bill of materials defined
                </div>
              )}
            </CardContent>
          </Card>

          <ManufacturedProductLocationManager
            product={product}
            onTransferComplete={() => loadProduct()}
          />
        </div>
      </div>

      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url}
        title={previewImage?.title || ''}
        description={previewImage?.description}
      />
    </div>
  );
};

export default ProductManufacturingDetailPage;